import { PaginationMetadata } from '@execonline-inc/resource';
import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import { emptyFragment } from '../../../../EmptyFragment';
import { translation, TranslationsContext } from '../../../../Translations';
import ParticipantsV2Store from '../Store';
import PageButton from './PageButton';
import { PageItem, pageItems } from './PageItems';
import StepButton from './StepButton';

interface Props {
  store: ParticipantsV2Store;
  metadata: PaginationMetadata;
}

const Item = ({
  item,
  store,
  current,
}: {
  item: PageItem;
  store: ParticipantsV2Store;
  current: number;
}) => {
  switch (item.kind) {
    case 'page':
      return (
        <li>
          <PageButton store={store} page={item.page} current={current} />
        </li>
      );
    case 'gap':
      return (
        <li className="px-2 text-muted-foreground" aria-hidden="true">
          &hellip;
        </li>
      );
    default:
      return assertNever(item);
  }
};

const itemKey = (item: PageItem): string => {
  switch (item.kind) {
    case 'page':
      return `page-${item.page}`;
    case 'gap':
      return `gap-${item.after}`;
    default:
      return assertNever(item);
  }
};

function Pages({ store, metadata }: Props) {
  const { currentPage, pageCount } = metadata;

  return pageCount > 1 ? (
    <TranslationsContext.Consumer>
      {(ts) => (
        <nav aria-label={translation('Pagination')(ts)}>
          <ol className="flex items-center gap-1">
            <li>
              <StepButton
                store={store}
                direction="previous"
                target={currentPage - 1}
                disabled={currentPage <= 1}
              />
            </li>
            {pageItems(currentPage, pageCount).map((item) => (
              <Item key={itemKey(item)} item={item} store={store} current={currentPage} />
            ))}
            <li>
              <StepButton
                store={store}
                direction="next"
                target={currentPage + 1}
                disabled={currentPage >= pageCount}
              />
            </li>
          </ol>
        </nav>
      )}
    </TranslationsContext.Consumer>
  ) : (
    emptyFragment()
  );
}

export default observer(Pages);
